import React, { useContext, useEffect, useState } from "react";
import { Container, Col, Row } from "react-bootstrap";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { TodosContext } from "../../../context";
import BreadCrumb from "../../../components/organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../components/organisms/Headline/Headline";
import Loader from "../../../components/atoms/Loader/Loader";
import LabelInsideInput from "../../../components/molecules/InputController/LabelInsideInputField";
import LabelInsideSelectField from "../../../components/molecules/InputController/LabelInsideSelectField";
import LabelInsideTextArea from "../../../components/atoms/InputController/LabelInside/Label-Inside-TextField-Controller/LabelInsideTextArea";
import SelectableCardGroup, {
  SelectableCardOption,
} from "../../../components/molecules/SelectableCardGroup/SelectableCardGroup";
import { handleMobileNumberChange } from "../../../utils/helpers/helperMethods/mobileNumberHandler";
import { OrganisationService } from "../service/organisationService";
import { COUNTRY_OPTIONS } from "../constants";
import { AUSTRALIA_COUNTRY_ID } from "../countries";

interface OptionType {
  value: string;
  label: string;
}

const EMAIL_FORMAT = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const defaultCountry =
  COUNTRY_OPTIONS.find((c) => c.value === String(AUSTRALIA_COUNTRY_ID)) ?? null;

// Serves both /organisations/add-organisation and /organisations/edit/:id.
const AddOrganisation = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { addAlert } = useContext(TodosContext);
  const [isLoading, setIsLoading] = useState(false);
  const [subscriptionPlanOptions, setSubscriptionPlanOptions] = useState<
    SelectableCardOption[]
  >([]);

  useEffect(() => {
    let isCancelled = false;
    OrganisationService.getSubscriptionPlans()
      .then((plans) => {
        if (isCancelled) return;
        setSubscriptionPlanOptions(
          plans.map((plan) => ({
            value: String(plan.id),
            title: plan.planName.toUpperCase(),
            subtitle: plan.planPurpose ?? "",
            description: plan.description,
          }))
        );
      })
      .catch(() => {
        if (!isCancelled)
          addAlert("Failed to load subscription plans", "error", true);
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,

    initialValues: {
      name: "",
      subscriptionPlanId: "",
      country: defaultCountry as OptionType | null,
      primaryContactFirstName: "",
      primaryContactLastName: "",
      primaryContactEmail: "",
      primaryContactPhone: "",
      note: "",
      isActive: true,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required("Please enter the organisation name")
        .max(200, "Organisation name must be 200 characters or less"),
      subscriptionPlanId: Yup.string().required(
        "Please select a subscription plan"
      ),
      country: Yup.object().nullable().required("Please select a country"),
      primaryContactFirstName: Yup.string()
        .trim()
        .required("Please enter the first name")
        .max(100, "First name must be 100 characters or less"),
      primaryContactLastName: Yup.string()
        .trim()
        .required("Please enter the last name")
        .max(100, "Last name must be 100 characters or less"),
      primaryContactEmail: Yup.string()
        .trim()
        .required("Please enter the primary contact email")
        .email("Please enter a valid email address")
        // Yup's email() accepts addresses without a domain suffix (e.g. "name@domain").
        .matches(EMAIL_FORMAT, "Please enter a valid email address")
        .max(100, "Email must be 100 characters or less"),
      primaryContactPhone: Yup.string()
        .required("Please enter the primary contact phone")
        .matches(/\d/, "Please enter the primary contact phone")
        .max(20, "Phone number must be 20 characters or less"),
      note: Yup.string().max(2000, "Note must be 2000 characters or less"),
    }),
    onSubmit: async (values) => {
      setIsLoading(true);
      const request = {
        name: values.name.trim(),
        subscriptionPlanId: Number(values.subscriptionPlanId),
        countryId: Number(values.country!.value),
        primaryContactFirstName: values.primaryContactFirstName.trim(),
        primaryContactLastName: values.primaryContactLastName.trim(),
        primaryContactEmail: values.primaryContactEmail.trim(),
        primaryContactPhone: values.primaryContactPhone,
        note: values.note.trim() || null,
        // New organisations are always created active.
        ...(isEdit && { isActive: values.isActive }),
      };
      try {
        if (isEdit) {
          await OrganisationService.updateOrganisation(id!, request);
          addAlert("Organisation Updated Successfully", "success", true);
        } else {
          await OrganisationService.createOrganisation(request);
          addAlert("Organisation Created Successfully", "success", true);
        }
        navigate("/organisations");
      } catch (e: any) {
        addAlert(
          e?.message ||
            (isEdit ? "Organisation Update Failed" : "Organisation Creation Failed"),
          "error",
          true
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  useEffect(() => {
    if (!id) return;
    let isCancelled = false;
    setIsLoading(true);
    OrganisationService.getOrganisation(id)
      .then((organisation) => {
        if (isCancelled) return;
        formik.setValues({
          name: organisation.name,
          subscriptionPlanId: organisation.subscriptionPlan
            ? String(organisation.subscriptionPlan.id)
            : "",
          country:
            COUNTRY_OPTIONS.find(
              (c) => c.value === String(organisation.countryId)
            ) ?? null,
          primaryContactFirstName: organisation.primaryContactFirstName,
          primaryContactLastName: organisation.primaryContactLastName,
          primaryContactEmail: organisation.primaryContactEmail,
          primaryContactPhone: organisation.primaryContactPhone,
          note: organisation.note ?? "",
          isActive: organisation.isActive ?? true,
        });
      })
      .catch(() => {
        if (!isCancelled) addAlert("Failed to load organisation", "error", true);
      })
      .finally(() => {
        if (!isCancelled) setIsLoading(false);
      });

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  return (
    <Container fluid className="layout-bar">
      <BreadCrumb />
      <Headline
        name={isEdit ? "Edit Organisation" : "Add New Organisation"}
        subHeading={
          isEdit
            ? "Update the organisation details."
            : "Create a organisation and assign the initial company administrator."
        }
      />

      <FormikProvider value={formik}>
        {/* noValidate: let Formik show its messages instead of the browser's email popup. */}
        <form onSubmit={formik.handleSubmit} noValidate>
          <div className="box-container">
            <Row>
              <Col xs={12} md={4}>
                <LabelInsideInput
                  id="name"
                  label="Organisation Name"
                  placeholder="Organisation Name"
                  onChange={formik.handleChange}
                  value={formik.values.name}
                  error={formik.errors.name}
                  touched={formik.touched.name}
                />
              </Col>
              <Col xs={12} md={4}>
                <LabelInsideSelectField
                  id="country"
                  name="country"
                  label="Country"
                  placeholder="Country"
                  onChange={(selected) =>
                    formik.setFieldValue("country", selected)
                  }
                  onBlur={() => formik.setFieldTouched("country", true)}
                  value={formik.values.country}
                  options={COUNTRY_OPTIONS}
                  error={formik.errors}
                  touched={formik.touched}
                />
              </Col>
              <Col xs={12} md={4}>
                <LabelInsideInput
                  id="primaryContactPhone"
                  type="tel"
                  label="Primary Contact Phone"
                  placeholder="+61 344 423 678"
                  onChange={(e) =>
                    formik.setFieldValue(
                      "primaryContactPhone",
                      handleMobileNumberChange(e.target.value)
                    )
                  }
                  value={formik.values.primaryContactPhone}
                  error={formik.errors.primaryContactPhone}
                  touched={formik.touched.primaryContactPhone}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4}>
                <LabelInsideInput
                  id="primaryContactFirstName"
                  label="Primary Contact First Name"
                  placeholder="First Name"
                  onChange={formik.handleChange}
                  value={formik.values.primaryContactFirstName}
                  error={formik.errors.primaryContactFirstName}
                  touched={formik.touched.primaryContactFirstName}
                />
              </Col>
              <Col xs={12} md={4}>
                <LabelInsideInput
                  id="primaryContactLastName"
                  label="Primary Contact Last Name"
                  placeholder="Last Name"
                  onChange={formik.handleChange}
                  value={formik.values.primaryContactLastName}
                  error={formik.errors.primaryContactLastName}
                  touched={formik.touched.primaryContactLastName}
                />
              </Col>
              <Col xs={12} md={4}>
                <LabelInsideInput
                  id="primaryContactEmail"
                  type="email"
                  label="Primary Contact Email"
                  placeholder="Primary Contact Email"
                  onChange={formik.handleChange}
                  value={formik.values.primaryContactEmail}
                  error={formik.errors.primaryContactEmail}
                  touched={formik.touched.primaryContactEmail}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={8}>
                <LabelInsideTextArea
                  name="note"
                  label="Note"
                  placeholder="Note"
                  type="text"
                  value={formik.values.note}
                  onChange={formik.handleChange}
                  errors={formik.errors}
                  touched={formik.touched}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <SelectableCardGroup
                  name="subscriptionPlanId"
                  label="Subscription Plan"
                  options={subscriptionPlanOptions}
                  value={formik.values.subscriptionPlanId}
                  onChange={(value) =>
                    formik.setFieldValue("subscriptionPlanId", value)
                  }
                  error={formik.errors.subscriptionPlanId}
                  touched={formik.touched.subscriptionPlanId}
                />
              </Col>
            </Row>
            {isEdit && (
              <Row>
                <Col xs={12} md={8}>
                  <div className="toggle-switch-field">
                    <span className="toggle-switch-field__label">Enable</span>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formik.values.isActive}
                        onChange={(e) =>
                          formik.setFieldValue("isActive", e.target.checked)
                        }
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </Col>
              </Row>
            )}
          </div>
          <div className="footer-btn-section d-flex justify-content-end box-container">
            <button
              className="btn btn-secondary me-3"
              type="button"
              onClick={() => navigate("/organisations")}
              disabled={isLoading}
            >
              Back
            </button>
            <button className="btn btn-primary" type="submit" disabled={isLoading}>
              {isEdit ? "Update Organisation" : "Create Organisation"}
            </button>
          </div>
        </form>
      </FormikProvider>

      {isLoading ? <Loader /> : null}
    </Container>
  );
};

export default AddOrganisation;
